import React, { useState, useEffect } from "react";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { Icons } from "../../../utils/icons";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import InviteModal from "../../modals/agent-invite-modal";
import { toast } from "../../ui/use-toast";
import TextController from "../../form-fields/TextController";
import SwitchController from "../../form-fields/SwitchController";
import ComboboxController from "../../form-fields/ComboboxController"; // ✅ Import ComboboxController
import Loader from "../../loader/loader";
import XanoAPI from "../../../lib/XanoAPI";
import TeamMembersList from "./team/team-member-list";
import RoleManagement from "./team/role-management";
import { useAuth } from "../../../lib/AuthContext";
import { getStates } from "../../../lib/utils";

interface FormValues {
  agencyName: string;
  totpRequired: boolean;
  excludedStates: string[];
}

const defaultValues: FormValues = {
  agencyName: "",
  totpRequired: false,
  excludedStates: [],
};

const MyTeam: React.FC = () => {
  const { currentUser } = useAuth();
  const [agencyId, setAgencyId] = useState<number | null>(null);
  const [agencyCreator, setAgencyCreator] = useState<number | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const methods = useForm<FormValues>({ defaultValues });

  /** ✅ Fetch Agency Data */
  const fetchAgencyData = async () => {
    try {
      setLoading(true);
      const api = XanoAPI.getInstance();
      const currentUser = await api.getCurrentUser();

      if (currentUser) {
        const { user_agency } = currentUser;
        const agency = user_agency[0];

        setAgencyId(agency.id);
        setAgencyCreator(agency.agency_creator);

        methods.reset({
          agencyName: agency.agency_name,
          totpRequired: agency.totp_required,
          excludedStates: agency.excludedStates || [],
        });

        const teamMembers = agency.members.map((user: any) => ({
          id: parseInt(user.id),
          name: user.name,
          email: user.email,
          role: agency.agency_creator === parseInt(user.id) ? "admin" : "agent",
          status: user.status as "active" | "deactivated" | "invited",
          permissions: user.user_permissions,
          permissions_id: user.permissions_id,
        }));

        setTeamMembers(teamMembers);
      }
    } catch (error) {
      console.error("Error fetching agency data:", error);
      toast({ title: "Error", description: "Failed to fetch agency data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencyData();
  }, [methods]);

  /** ✅ Handle Agency Update */
  const handleAgencyUpdate: SubmitHandler<FormValues> = async (data) => {
    try {
      const api = XanoAPI.getInstance();
      if (agencyId) {
        await api.updateAgencyDetails(
          agencyId,
          data.agencyName,
          data.totpRequired
        );
        toast({
          title: "Agency Updated",
          description:
            "The agency name and settings have been successfully updated.",
        });
      }
    } catch (error) {
      console.error("Error updating agency settings:", error);
      toast({
        title: "Update Failed",
        description: "There was an error updating the agency settings.",
      });
    }
  };

  /** ✅ Handle Team Member Invitation */
  const handleInvite: SubmitHandler<any> = async (data) => {
    try {
      const api = XanoAPI.getInstance();
      await api.sendInviteEmail(data.email, data.name);

      const newMember: TeamMember = {
        id: Date.now(),
        name: data.name,
        email: data.email,
        agency_id: agencyId,
        role: "agent",
        status: "invited",
        permissions: data.permissions || [],
        permissions_id: data.permissions_id,
        members: data.members ?? [],
        walkthrough_created: data.walkthrough_created ?? false,
      };

      setTeamMembers([...teamMembers, newMember]);
      toast({
        title: "Invite Sent",
        description: "An invitation has been sent to the team member.",
      });
      setInviteModalOpen(false);
    } catch (error) {
      console.error("Error sending invite:", error);
      toast({
        title: "Invite Failed",
        description: "There was an error sending the invitation.",
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <div id="my-team" className="space-y-6">
        <h2 className="text-xl font-semibold text-accent">My Team</h2>

        {/* ✅ Agency Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Agency Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={methods.handleSubmit(handleAgencyUpdate)}
              className="space-y-4"
            >
              <TextController
                name="agencyName"
                label="Agency Name"
                placeholder="Enter agency name"
                defaultValue={currentUser?.user_agency[0].agency_name}
              />
              <SwitchController
                name="totpRequired"
                label="Require Two-Factor Authentication"
                infoMessage="Toggle to require all team members to enable TOTP."
                defaultValue={false}
              />
              <Button type="submit" className="bg-accent">
                <Icons.save className="mr-2" />
                Save Settings
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* ✅ State Exclusion Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Exclude States</CardTitle>
          </CardHeader>
          <CardContent>
            <ComboboxController
              name="excludedStates"
              label="Excluded States"
              options={getStates()}
              defaultValue={[]} // ✅ Now supports arrays
              multiple={true} // ✅ Enables multi-select
              onChange={(selected) => {
                methods.setValue(
                  "excludedStates",
                  Array.isArray(selected) ? selected : [selected]
                ); // ✅ Always set an array
              }}
            />
          </CardContent>
        </Card>

        {/* ✅ Team Members Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Team Members</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TeamMembersList
                  teamMembers={teamMembers}
                  agencyCreator={agencyCreator}
                  fetchAgencyNameAndMembers={fetchAgencyData}
                />
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button
              onClick={() => setInviteModalOpen(true)}
              className="bg-accent"
            >
              <Icons.addUser className="mr-2" />
              Invite Team Member
            </Button>
          </CardFooter>
        </Card>

        {/* ✅ Modals */}
        <InviteModal
          isOpen={isInviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          onInvite={handleInvite}
        />

        {/* ✅ Role Management */}
        {agencyId && <RoleManagement agencyId={agencyId} />}
      </div>
    </FormProvider>
  );
};

export default MyTeam;
